import { MulticastChannel } from 'redux-saga';
import { call, takeEvery } from 'typed-redux-saga';

import { getAudioContext } from '../../../../helpers/audio-context';
import { Logger } from '../../../../helpers/loggerFacade';
import { multicastTakeEvery } from '../../../../helpers/redux-saga/multicast-effects';
import { RoomAgentError } from '../../errors/RoomAgentError';
import { RecorderManager } from '../../recorder/RecorderManager';
import { CallMetaData, SDKAdapter } from '../../types';
import { RoomAgentAudioProcessor } from './RoomAgentAudioProcessor';

export function* mediaSaga({
    recorderManager,
    adapter,
    onRemoteTrackChannel,
    callMetaData,
    logger,
    audioProcessingEnabled,
}: {
    recorderManager: RecorderManager;
    adapter: SDKAdapter;
    onRemoteTrackChannel: MulticastChannel<{ track: MediaStreamTrack }>;
    callMetaData?: CallMetaData;
    logger: Logger;
    audioProcessingEnabled?: boolean;
}) {
    yield* takeEvery(adapter.addTrackChannel, (remoteTrack) => {
        logger.debug('remote track room-agent add track', remoteTrack);

        if (remoteTrack && remoteTrack.kind === 'audio') {
            recorderManager.getMainAudioRecorder().addTrack(remoteTrack);
        }
    });

    yield* takeEvery(adapter.removeTrackChannel, (remoteTrack) => {
        logger.debug('remote track room-agent remover track', remoteTrack);

        if (remoteTrack && remoteTrack.kind === 'audio') {
            recorderManager.getMainAudioRecorder().removeTrack(remoteTrack);
        }
    });

    yield* multicastTakeEvery(onRemoteTrackChannel, function* ({ track }) {
        const audioCtx = getAudioContext();

        if (!audioCtx) {
            return;
        }

        const roomAgentAudioProcessor = new RoomAgentAudioProcessor({ audioCtx, logger });
        logger.debug('updating local tracks', track);

        if (track.kind === 'audio' && audioProcessingEnabled) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).__ROOMAGENT_AUDIO_PROCESSOR = roomAgentAudioProcessor;
            roomAgentAudioProcessor.replaceTrack(track);
        }

        const localTrack =
            track.kind === 'audio' && audioProcessingEnabled ? roomAgentAudioProcessor.getProcessedTrack() : track;
        yield* call([adapter, adapter.setLocalTrack], localTrack);
    });

    if (callMetaData?.viewportHeight || callMetaData?.viewportWidth) {
        try {
            const width = callMetaData?.viewportWidth;
            const height = callMetaData?.viewportHeight;

            logger.debug(`applying video constraints width: ${width}, height: ${height}`);

            const mainVideoRecorder = recorderManager.getMainVideoRecorder();

            yield* call([mainVideoRecorder, mainVideoRecorder.applyConstraints], {
                width,
                height,
            });
        } catch (error) {
            logger.error(RoomAgentError.from(error, { method: 'videoRecorder.applyConstraints' }));
        }
    }
}
